import React from 'react';

import { FacebookRegularIcon } from '@change/ds-icons/FacebookRegularIcon';
import { InstagramIcon } from '@change/ds-icons/InstagramIcon';
import { NewspaperIcon } from '@change/ds-icons/NewspaperIcon';
import { RedditIcon } from '@change/ds-icons/RedditIcon';
import { TikTokIcon } from '@change/ds-icons/TikTokIcon';
import { TwitterIcon } from '@change/ds-icons/TwitterIcon';
import type { Icon } from '@change/ds-icons/types';
import { YouTubeIcon } from '@change/ds-icons/YouTubeIcon';

import nytimesLogo from '#src/app/pages/homepage/shared/assets/nytimes-logo.png';
import type { SourceIconStackItem } from '#src/app/pages/homepage/shared/components';

import type { SocialPostPlatformId } from '../../hooks/useSocialPostUrlForm/platforms';

type IconComponent = React.ComponentType<Icon>;

/** One icon per allowlisted source; publications without a brand icon share the newspaper. */
const PLATFORM_ICONS: Readonly<Record<SocialPostPlatformId, IconComponent>> = {
	tiktok: TikTokIcon,
	instagram: InstagramIcon,
	facebook: FacebookRegularIcon,
	reddit: RedditIcon,
	x: TwitterIcon,
	youtube: YouTubeIcon,
	substack: NewspaperIcon,
	nytimes: NewspaperIcon,
};

/** Each source's badge in the helper's rotation, or null to leave it out; keyed so a new source forces a decision. */
const HELPER_BADGES: Readonly<Record<SocialPostPlatformId, SourceIconStackItem | null>> = {
	facebook: { kind: 'social', variant: 'facebook', icon: <FacebookRegularIcon /> },
	instagram: { kind: 'social', variant: 'instagram', icon: <InstagramIcon /> },
	youtube: { kind: 'icon', icon: YouTubeIcon, className: 'bg-[#FF0000] text-icon-inverse-strong-fixed' },
	reddit: { kind: 'social', variant: 'reddit', icon: <RedditIcon /> },
	tiktok: { kind: 'icon', icon: TikTokIcon, className: 'bg-[#000000] text-icon-inverse-strong-fixed' },
	x: { kind: 'social', variant: 'twitter', icon: <TwitterIcon /> },
	// brand hex backgrounds don't flip with the theme, so their marks use the fixed (always-white) token
	// white mark on a transparent background
	nytimes: { kind: 'image', src: nytimesLogo, className: 'bg-[#000000] p-1' },
	substack: null,
};

export const HELPER_SOURCE_ITEMS: readonly SourceIconStackItem[] = Object.values(HELPER_BADGES).filter(
	(badge): badge is SourceIconStackItem => badge !== null,
);

/** The icon for an allowlisted source. */
export const SourceIcon: React.FC<Props> = ({ platform, ...iconProps }) => {
	const PlatformIcon = PLATFORM_ICONS[platform];
	return <PlatformIcon {...iconProps} />;
};

type Props = Icon & Readonly<{ platform: SocialPostPlatformId }>;
